import { useReducedMotion } from '../hooks/useReducedMotion';

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  circle?: boolean;
  className?: string;
  count?: number;
}

export function Skeleton({
  width = '100%',
  height = '1rem',
  circle = false,
  className = '',
  count = 1
}: SkeletonProps) {
  const prefersReducedMotion = useReducedMotion();
  
  const skeletonStyle = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: circle ? (typeof width === 'number' ? `${width}px` : width) : (typeof height === 'number' ? `${height}px` : height),
    borderRadius: circle ? '50%' : '4px',
  };

  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className={`skeleton ${className}`}
          style={skeletonStyle}
          aria-hidden="true"
          aria-busy="true"
        >
          {!prefersReducedMotion && (
            <div className="skeleton-shimmer" />
          )}
        </div>
      ))}
    </>
  );
}

// Card Skeleton
export function CardSkeleton() {
  return (
    <div 
      className="glass-card p-6 space-y-4" 
      aria-busy="true" 
      aria-label="Loading content"
    >
      <div className="flex items-center gap-4">
        <Skeleton circle width={48} height={48} />
        <div className="flex-1 space-y-2">
          <Skeleton width="60%" height={24} />
          <Skeleton width="40%" height={16} />
        </div>
      </div>
      <Skeleton width="90%" height={16} count={2} />
    </div>
  );
}

// Audio Player Skeleton
export function AudioPlayerSkeleton() {
  return (
    <div 
      className="glass-card p-4 space-y-4" 
      aria-busy="true"
      aria-label="Loading audio player"
    >
      <div className="flex items-center gap-4">
        <Skeleton circle width={40} height={40} />
        <div className="flex-1">
          <Skeleton width="100%" height={60} />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <Skeleton width={60} height={16} />
        <div className="flex gap-2">
          <Skeleton circle width={32} height={32} count={3} />
        </div>
        <Skeleton width={60} height={16} />
      </div>
    </div>
  );
}

// Feature Card Skeleton
export function FeatureCardSkeleton() {
  return (
    <div 
      className="glass-card p-6 space-y-4" 
      aria-busy="true"
      aria-label="Loading feature"
    >
      <Skeleton circle width={48} height={48} />
      <Skeleton width="70%" height={24} />
      <Skeleton width="100%" height={16} count={2} />
      <Skeleton width="40%" height={20} />
    </div>
  );
}

// Stats Skeleton
export function StatsSkeleton() {
  return (
    <div className="flex items-center justify-center gap-8 md:gap-16" aria-busy="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="text-center space-y-2">
          <Skeleton width={80} height={32} />
          <Skeleton width={60} height={16} />
        </div>
      ))}
    </div>
  );
}

// Form Field Skeleton
export function FormFieldSkeleton() {
  return (
    <div className="space-y-2" aria-busy="true">
      <Skeleton width={120} height={16} />
      <Skeleton width="100%" height={48} />
    </div>
  );
}

// Table Skeleton
export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Loading table">
      <div className="flex gap-4 pb-2 border-b border-white/10">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} width="25%" height={24} />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4 py-3">
          {Array.from({ length: 4 }).map((_, j) => (
            <Skeleton key={j} width="25%" height={20} />
          ))}
        </div>
      ))}
    </div>
  );
}

// Waveform Skeleton
export function WaveformSkeleton() {
  return (
    <div 
      className="w-full h-24 flex items-center justify-center gap-1" 
      aria-busy="true"
      aria-label="Loading waveform"
    >
      {Array.from({ length: 50 }).map((_, i) => (
        <Skeleton
          key={i}
          width={4}
          height={Math.random() * 60 + 20}
          className="!rounded-full"
        />
      ))}
    </div>
  );
}

export default Skeleton;
